/**
 * CassKai - Point d'entrée unique pour afficher une erreur traduite.
 *
 * ⚠️ CE MODULE NE DOIT DÉPENDRE NI DE REACT NI D'AUCUN COMPOSANT. Il ne fait
 * que publier un évènement ; c'est `ErrorSupportProvider` qui l'écoute et
 * décide du toast ou du dialogue.
 *
 * Cette contrainte n'est pas cosmétique. La première version réexportait
 * `showTranslatedError` depuis `ErrorSupportProvider`, lequel importe
 * `toastError` depuis `toast-helpers` — qui importe ce fichier. Cela formait
 * un cycle `toast-helpers → error-toast → ErrorSupportProvider → toast-helpers`
 * et, surtout, faisait entrer le dialogue de support, Radix, le contexte
 * Entreprise et le service de tickets dans TOUT module important un helper de
 * toast, c'est-à-dire presque toute l'application. Les trente sites d'appel
 * passent par ici : ce fichier doit rester le plus léger du dépôt.
 */

export interface ShowErrorOptions {
  /** Ce que l'utilisateur essayait de faire, pour composer un message naturel. */
  contexteMetier?: string;
  /** Nom de l'écran concerné, utilisé dans le détail technique. */
  ecran?: string;
  /**
   * true  : toujours ouvrir le dialogue de support (erreur bloquante) ;
   * false : toujours rester en toast, même si l'erreur est inattendue.
   * Par défaut, un cas inattendu ouvre le dialogue.
   */
  bloquant?: boolean;
}

/** Nom de l'évènement écouté par `ErrorSupportProvider`. */
export const SHOW_ERROR_EVENT = 'casskai:show-error';

export interface ShowErrorEventDetail {
  error: unknown;
  options?: ShowErrorOptions;
}

/**
 * Déclenche une erreur traduite depuis n'importe où dans l'application, y
 * compris hors d'un composant React. Le provider affichera un toast ou le
 * dialogue de support selon la gravité de l'erreur.
 */
export function showTranslatedError(error: unknown, options?: ShowErrorOptions): void {
  if (typeof window === 'undefined' || typeof CustomEvent === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent<ShowErrorEventDetail>(SHOW_ERROR_EVENT, {
      detail: { error, options },
      bubbles: true,
      cancelable: true,
    })
  );
}
